/**
 * Framework support: FRAMEWORK-AGNOSTIC.
 *
 * Safe to import from Next.js (server + client bundles), Vite SPAs, Electron
 * renderers, and Node-based tests. Env resolution is handled by the shared
 * helper in `./resolve-env` which probes `import.meta.env` and `process.env`
 * defensively.
 */
import { createBrowserClient as createSsrBrowserClient } from '@supabase/ssr'
import { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './database.types'
import { sharedCookieDomainForHost } from './domain'
import { publicFetchOption } from './public-key'
import { resolveSupabaseEnv, type SupabaseEnvConfig } from './resolve-env'

// Singleton instance to prevent creating multiple clients
let clientInstance: SupabaseClient<Database> | null = null;

// Request deduplication: prevents identical requests within the same tick
const pendingRequests = new Map<string, Promise<unknown>>();

/**
 * Optional overrides for createClient / createBrowserClient.
 *
 * When both `url` and `anonKey` are provided, env resolution is skipped entirely.
 * When either is missing, the env-resolution fallback chain runs (Vite → Next).
 */
export interface BrowserClientConfig extends SupabaseEnvConfig {
  /**
   * Set to `false` on apps that only READ an existing session
   * (e.g. landing-page navbar avatar). When `true` (default), supabase-js
   * autorefreshes the access token; on the shared `.thekairos.app` cookie,
   * a stale refresh token returns 400 → the SDK calls signOut → it clears
   * the cookie that the dashboard session also depends on. Read-only
   * consumers MUST opt out.
   */
  autoRefreshToken?: boolean;
}

// Clean up old storage key (one-time cleanup)
// This ensures users will need to sign in again with the correct storage key
function cleanupOldStorageKey() {
  if (typeof window === 'undefined') return;
  if (typeof localStorage === 'undefined') return;

  const oldKey = 'kairos-auth-token';
  const hasOldKey = localStorage.getItem(oldKey);

  if (hasOldKey) {
    console.log('🔧 Cleaning up old auth storage key. Please sign in again.');
    localStorage.removeItem(oldKey);
    // Clear any related session data
    Object.keys(localStorage).forEach(key => {
      if (key.includes('kairos-auth')) {
        localStorage.removeItem(key);
      }
    });
  }
}

export function createClient(config?: BrowserClientConfig): SupabaseClient<Database> {
  // Clean up old storage key on first client creation
  cleanupOldStorageKey();

  // Return existing instance if available (singleton pattern).
  // Note: singleton is keyed by module load, not by config — first call wins.
  // This matches the pre-existing behavior the Next dashboard relies on.
  if (clientInstance) {
    return clientInstance;
  }

  const { url: supabaseUrl, anonKey: supabaseAnonKey } = resolveSupabaseEnv(config, {
    subpath: 'browser',
  });

  // Determine cookie domain for cross-subdomain session sharing.
  // Only set a shared parent domain when actually running on a known Kairos/Klarora
  // host family — this ensures token refreshes write to the shared domain so
  // finance/crm/dashboard all stay in sync.
  //
  // We check the actual hostname rather than NODE_ENV so that:
  //   - Electron (file://, hostname='') → undefined (no cross-domain needed)
  //   - localhost dev → undefined
  //   - Any *.thekairos.app subdomain → '.thekairos.app' ✓
  //   - Any *.klarora.app subdomain   → '.klarora.app' ✓
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const cookieDomain = sharedCookieDomainForHost(hostname)

  // Create client with optimized settings to reduce unnecessary requests.
  //
  // NOTE: We only spread `domain` when a value was resolved. Under
  // `exactOptionalPropertyTypes`, passing `domain: undefined` explicitly is a
  // type error because the underlying `cookie.SerializeOptions.domain` is
  // typed as `domain?: string` (not `string | undefined`).
  clientInstance = createSsrBrowserClient<Database>(
    supabaseUrl,
    supabaseAnonKey,
    {
      ...publicFetchOption(supabaseAnonKey),
      cookieOptions: {
        ...(cookieDomain ? { domain: cookieDomain } : {}),
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production',
        path: '/',
      },
      auth: {
        // Default: autorefresh enabled. Apps that share the cross-subdomain
        // cookie but only need to *read* a session (e.g. landing navbar) MUST
        // pass `autoRefreshToken: false` to avoid wiping the dashboard cookie
        // on a stale refresh token.
        autoRefreshToken: config?.autoRefreshToken ?? true,
        // Persist session in localStorage
        persistSession: true,
        // Detect session in URL (for OAuth callbacks)
        detectSessionInUrl: true,
        // Use default storage key for consistency with server-side
        // Note: Custom storage keys can cause auth session mismatch in server actions
      },
      // Reduce realtime connection overhead
      realtime: {
        params: {
          eventsPerSecond: 2
        }
      }
    }
  ) as SupabaseClient<Database>;

  return clientInstance;
}

/**
 * Alias for createClient() - returns the singleton Supabase client
 */
export function getClient(config?: BrowserClientConfig): SupabaseClient<Database> {
  return createClient(config);
}

/**
 * Named export matching the cross-framework public API spec.
 *
 * Equivalent to `createClient(config)` — kept as a separate export so that
 * apps can `import { createBrowserClient } from '@kairos/supabase/browser'`
 * regardless of whether they're on Next.js or Vite.
 */
export function createBrowserClient(config?: BrowserClientConfig): SupabaseClient<Database> {
  return createClient(config);
}

/**
 * Coalesce identical concurrent requests to prevent duplicates.
 * If the same request (by key) is made while a previous one is still pending,
 * the pending promise is returned instead of making a new request.
 * 
 * @param key - Unique identifier for this request (e.g., "tasks:team123")
 * @param fetcher - Function that performs the actual fetch
 * @param coalesceTtlMs - How long to keep the request coalesced (default 100ms)
 * @returns Promise resolving to the fetched data
 * 
 * @example
 * const tasks = await coalescedFetch(
 *   `tasks:${teamId}`,
 *   () => supabase.from('tasks').select('*').eq('team_id', teamId)
 * );
 */
export async function coalescedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  coalesceTtlMs = 100
): Promise<T> {
  // Return existing pending request if available
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key) as Promise<T>;
  }

  // Create new request and track it
  const promise = fetcher().finally(() => {
    // Clean up after a short delay to allow for micro-task coalescing
    setTimeout(() => pendingRequests.delete(key), coalesceTtlMs);
  });

  pendingRequests.set(key, promise);
  return promise;
}

/**
 * Check if a request with the given key is currently pending
 */
export function isRequestPending(key: string): boolean {
  return pendingRequests.has(key);
}

/**
 * Clear all pending requests (useful for cleanup on logout)
 */
export function clearPendingRequests(): void {
  pendingRequests.clear();
}
